// In-memory access token — never written to localStorage.
// Lost on page refresh, recovered via silentRefresh() on app mount.
let inMemoryAccessToken: string | null = null

export function getInMemoryToken(): string | null {
  return inMemoryAccessToken
}

export function setInMemoryToken(token: string | null): void {
  inMemoryAccessToken = token
}

const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8080'

// Calls POST /refresh with credentials: 'include' so the browser automatically
// sends the httpOnly refresh token cookie. No JavaScript ever reads the cookie.
// Deduplicates concurrent calls so a double-invoke (e.g. React StrictMode) only
// hits the server once.
let refreshInFlight: Promise<string | null> | null = null

// Authenticated fetch wrapper — adds Bearer header and retries once on 403
// by calling silentRefresh(). Use this for any raw fetch that needs auth.
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getInMemoryToken()
  if (!token) throw new Error('Authentication token missing. Please log in again.')

  const withToken = (t: string): RequestInit => ({
    ...options,
    headers: { ...options.headers, Authorization: `Bearer ${t}` },
  })

  let response = await fetch(url, withToken(token))

  if (response.status === 403) {
    const newToken = await silentRefresh()
    if (!newToken) throw new Error('Session expired. Please log in again.')
    response = await fetch(url, withToken(newToken))
  }

  return response
}

export function silentRefresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${apiBase}/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) return null
      const json = await res.json()
      const token = json?.accessToken ?? null
      if (token) setInMemoryToken(token)
      return token
    } catch {
      return null
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}
